<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'InactivityTimeouts',
  components: { NextButton },
  props: {
    inbox: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      transferEnabled: false,
      transferMinutes: 3,
      inactiveEnabled: true,
      inactiveMinutes: 10,
      closeEnabled: true,
      closeMinutes: 15,
      isSaving: false,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
  },
  watch: {
    inbox: {
      immediate: true,
      handler() {
        this.loadFromInbox();
      },
    },
  },
  methods: {
    loadFromInbox() {
      const cfg = this.inbox.widget_theme_config?.inactivityTimeouts || {};
      this.transferEnabled = cfg.transferEnabled ?? false;
      this.transferMinutes = cfg.transferMinutes ?? 3;
      this.inactiveEnabled = cfg.inactiveEnabled ?? true;
      this.inactiveMinutes = cfg.inactiveMinutes ?? 10;
      this.closeEnabled = cfg.closeEnabled ?? true;
      this.closeMinutes = cfg.closeMinutes ?? 15;
    },
    cancel() {
      this.loadFromInbox();
    },
    async save() {
      this.isSaving = true;
      try {
        const existingTheme = this.inbox.widget_theme_config || {};
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            widget_theme_config: {
              ...existingTheme,
              inactivityTimeouts: {
                transferEnabled: this.transferEnabled,
                transferMinutes: Number(this.transferMinutes) || 3,
                inactiveEnabled: this.inactiveEnabled,
                inactiveMinutes: Number(this.inactiveMinutes) || 10,
                closeEnabled: this.closeEnabled,
                closeMinutes: Number(this.closeMinutes) || 15,
              },
            },
          },
        });
        useAlert('Inactivity timeouts saved');
      } catch (e) {
        useAlert('Unable to save inactivity timeouts');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="it-page">
    <div class="it-body">
      <div class="it-header">
        <h2 class="it-title">Inactivity timeouts</h2>
      </div>

      <!-- Rule 1: transfer -->
      <label class="it-row" :class="{ 'it-row--disabled': !transferEnabled }">
        <div class="it-checkbox-wrap">
          <input
            v-model="transferEnabled"
            type="checkbox"
            class="it-checkbox"
          />
          <span
            class="it-checkbox-custom"
            :class="{ checked: transferEnabled }"
          >
            <svg
              v-if="transferEnabled"
              viewBox="0 0 12 10"
              fill="none"
              width="12"
              height="10"
            >
              <path
                d="M1 5l3.5 3.5L11 1"
                stroke="#fff"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </div>
        <span class="it-text">
          When the agent is not responding for
          <span class="it-input-wrap">
            <input
              v-model.number="transferMinutes"
              type="number"
              min="1"
              max="120"
              class="it-number-input"
              :disabled="!transferEnabled"
            />
          </span>
          minutes, <strong>transfer the visitor to another agent.</strong>
        </span>
      </label>
      <p class="it-hint">
        Applies only if the chat has just started. All following responses can
        be longer and won't result in a transfer.
      </p>

      <!-- Rule 2: make inactive -->
      <label class="it-row" :class="{ 'it-row--disabled': !inactiveEnabled }">
        <div class="it-checkbox-wrap">
          <input
            v-model="inactiveEnabled"
            type="checkbox"
            class="it-checkbox"
          />
          <span
            class="it-checkbox-custom"
            :class="{ checked: inactiveEnabled }"
          >
            <svg
              v-if="inactiveEnabled"
              viewBox="0 0 12 10"
              fill="none"
              width="12"
              height="10"
            >
              <path
                d="M1 5l3.5 3.5L11 1"
                stroke="#fff"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </div>
        <span class="it-text">
          When there are no new messages in the conversation for
          <span class="it-input-wrap">
            <input
              v-model.number="inactiveMinutes"
              type="number"
              min="1"
              max="1440"
              class="it-number-input"
              :disabled="!inactiveEnabled"
            />
          </span>
          minutes, <strong>move the conversation to Pending.</strong>
        </span>
      </label>
      <p class="it-hint">
        Pending conversations are treated as inactive and can be followed up
        later.
      </p>

      <!-- Rule 3: close chat -->
      <label class="it-row" :class="{ 'it-row--disabled': !closeEnabled }">
        <div class="it-checkbox-wrap">
          <input v-model="closeEnabled" type="checkbox" class="it-checkbox" />
          <span class="it-checkbox-custom" :class="{ checked: closeEnabled }">
            <svg
              v-if="closeEnabled"
              viewBox="0 0 12 10"
              fill="none"
              width="12"
              height="10"
            >
              <path
                d="M1 5l3.5 3.5L11 1"
                stroke="#fff"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </div>
        <span class="it-text">
          When there are no new messages in the chat for
          <span class="it-input-wrap">
            <input
              v-model.number="closeMinutes"
              type="number"
              min="1"
              max="1440"
              class="it-number-input"
              :disabled="!closeEnabled"
            />
          </span>
          minutes, <strong>close the chat.</strong>
        </span>
      </label>

      <!-- Footer -->
      <div class="it-footer">
        <NextButton
          size="sm"
          type="button"
          label="Save changes"
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="save"
        />
        <button class="it-discard-btn" type="button" @click="cancel">
          Discard changes
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.it-page {
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
}

.it-body {
  max-width: 720px;
  margin: 0;
  padding: 32px 32px 80px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.it-header {
  margin-bottom: 24px;
}
.it-title {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.it-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 0;
  cursor: pointer;
  border-top: 1px solid #f3f4f6;
}
.it-row:first-of-type {
  border-top: none;
}
.it-row--disabled {
  opacity: 0.55;
}

.it-checkbox-wrap {
  position: relative;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 9px;
}
.it-checkbox {
  position: absolute;
  opacity: 0;
  inset: 0;
  cursor: pointer;
  margin: 0;
  width: 100%;
  height: 100%;
}
.it-checkbox-custom {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid #d1d5db;
  background: #ffffff;
  pointer-events: none;
  transition:
    background 0.15s,
    border-color 0.15s;
}
.it-checkbox-custom.checked {
  background: #4384f5;
  border-color: #4384f5;
}

.it-text {
  font-size: 12px;
  color: #111827;
  line-height: 1.55;
  flex: 1;
}
.it-text strong {
  font-weight: 600;
}

.it-input-wrap {
  display: inline-block;
  margin: 0 4px;
}
.it-number-input {
  width: 52px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0 6px;
  font-size: 12px;
  font-weight: 600;
  color: #111827;
  background: #ffffff;
  text-align: center;
  outline: none;
  -moz-appearance: textfield;
}
.it-number-input::-webkit-outer-spin-button,
.it-number-input::-webkit-inner-spin-button {
  -webkit-appearance: inner-spin-button;
  opacity: 1;
}
.it-number-input:focus {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.15);
}
.it-number-input:disabled {
  opacity: 0.4;
}

.it-hint {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  margin: -8px 0 8px 32px;
  line-height: 1.4;
}

.it-footer {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-top: 24px;
  margin-top: 8px;
  border-top: 1px solid #e5e7eb;
}
.it-discard-btn {
  padding: 9px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s;
}
.it-discard-btn:hover {
  background: #f9fafb;
}
</style>
